// บันทึกการรับ SO จาก Venio: what happened to each Sale Order and, when it did not
// become a NetSuite order, why (in Thai). Admins see every SO; sales see their own.
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';
import PageLoader from './PageLoader';

const OUTCOMES = {
  netsuite_created: { label: 'สร้างใน NetSuite แล้ว', tone: 'ok', icon: 'bi-check-circle-fill' },
  rental_saved: { label: 'เข้ารายการเครื่องเช่า', tone: 'ok', icon: 'bi-file-earmark-text-fill' },
  cancelled: { label: 'ยกเลิก SO ใน Venio', tone: 'bad', icon: 'bi-x-octagon-fill' },
  failed: { label: 'ไม่สำเร็จ', tone: 'bad', icon: 'bi-exclamation-triangle-fill' }
};

const fmtTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('th-TH', { day: 'numeric', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const WebhookLog = () => {
  const [events, setEvents] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [outcome, setOutcome] = useState('');
  const [query, setQuery] = useState('');

  const load = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const resp = await axios.get(`${API_URL}/webhook/events`, { params: { outcome, q: query.trim() } });
      setEvents(resp.data.events || []);
      setIsAdmin(Boolean(resp.data.isAdmin));
      setError('');
    } catch (err) {
      setError('โหลดบันทึกไม่สำเร็จ กรุณาลองใหม่');
    } finally {
      setLoading(false);
    }
  }, [outcome, query]);

  useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  // new SOs arrive all day, so refresh quietly while the page is open
  useEffect(() => {
    const t = setInterval(() => load(true), 30000);
    return () => clearInterval(t);
  }, [load]);

  if (loading && events.length === 0) return <PageLoader message="กำลังโหลดบันทึกการรับ SO..." />;

  const problems = events.filter(e => e.outcome === 'failed' || e.outcome === 'cancelled').length;

  return (
    <div className="rs wlog">
      <header className="rs-header">
        <div>
          <h1 className="rs-title">บันทึกการรับ SO</h1>
          <p className="rs-subtitle">
            {isAdmin ? 'ใบสั่งขายทั้งหมดที่ Venio ส่งเข้ามา' : 'ใบสั่งขายของคุณที่ Venio ส่งเข้ามา'} · ถ้า SO ไม่ถูกสร้าง จะบอกสาเหตุที่นี่
          </p>
        </div>
        <button type="button" className="rs-btn rs-btn--ghost" onClick={() => load()}>
          <i className="bi bi-arrow-clockwise"></i> รีเฟรช
        </button>
      </header>

      <div className="wlog__filters">
        <div className="rs-search" style={{ flex: '1 1 260px' }}>
          <i className="bi bi-search"></i>
          <input className="rs-input" type="text" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="ค้นหาเลข SO ชื่อเรื่อง หรือสาเหตุ..." aria-label="ค้นหา" />
        </div>
        <select className="rs-input" style={{ flex: '0 1 220px' }} value={outcome} onChange={(e) => setOutcome(e.target.value)} aria-label="กรองตามผล">
          <option value="">ทุกผลลัพธ์</option>
          {Object.entries(OUTCOMES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      {error && <div className="econ-alert" role="alert"><div className="econ-alert__line">{error}</div></div>}

      {events.length === 0 ? (
        <div className="rs-card" style={{ textAlign: 'center', color: '#65645f' }}>
          ยังไม่มีบันทึก — เมื่อมี SO ใหม่จาก Venio จะแสดงที่นี่
        </div>
      ) : (
        <>
          {problems > 0 && (
            <div className="wlog__summary"><i className="bi bi-exclamation-triangle"></i> {problems} รายการที่ไม่ถูกสร้างหรือถูกยกเลิก (ดูสาเหตุด้านล่าง)</div>
          )}
          <div className="rs-card" style={{ padding: 0 }}>
            <div className="rs-table-wrap" style={{ border: 0 }}>
              <table className="rs-table">
                <thead>
                  <tr>
                    <th>เวลา</th><th>เลข SO</th><th>เรื่อง</th>{isAdmin && <th>พนักงาน</th>}<th>ผล</th><th>รายละเอียด / สาเหตุ</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map(e => {
                    const o = OUTCOMES[e.outcome] || { label: e.outcome, tone: 'gray', icon: 'bi-dot' };
                    return (
                      <tr key={e.id}>
                        <td style={{ whiteSpace: 'nowrap' }}>{fmtTime(e.received_at)}</td>
                        <td className="rs-mono">{e.so_number}</td>
                        <td>{e.subject || '—'}</td>
                        {isAdmin && <td className="rs-mono">{e.staff_code || '—'}</td>}
                        <td><span className={`wlog__pill wlog__pill--${o.tone}`}><i className={`bi ${o.icon}`}></i> {o.label}</span></td>
                        <td>
                          {e.reason_th || '—'}
                          {e.rental_order_id && (
                            <> · <Link className="econ-link-btn" to={`/rental-so/${e.rental_order_id}`}>เปิดออเดอร์เช่า</Link></>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default WebhookLog;
